import { Map as OlMap } from 'ol';
import { Group as LayerGroup, Layer as BaseLayer, Vector as VectorLayer } from 'ol/layer.js';
import { ImageWMS, TileWMS, Vector as VectorSource, WMTS, XYZ } from 'ol/source.js';
import { get as getProjection, Projection } from 'ol/proj.js';
import { LayerManagerModel, LayerTypes, WMSLayerModel } from '../models';
import { OlLayerHelper, WmsServiceParamsModel } from '../helpers/ol-layer.helper';
import { LayerModel } from '../models/layer.model';
import { VectorLayerModel } from '../models/vector-layer.model';
import { isOpenLayersVectorLayer, isOpenLayersWMSLayer, isPossibleRealtimeLayer } from '../helpers/ol-layer-types.helper';
import { LayerTypesHelper } from '../helpers/layer-types.helper';
import { ArrayHelper } from '@tailormap-viewer/shared';
import { NgZone } from '@angular/core';
import { HttpXsrfTokenExtractor } from '@angular/common/http';
import { ServerType } from '@tailormap-viewer/api';

export class OpenLayersLayerManager implements LayerManagerModel {

  private layers: Map<string, BaseLayer> = new Map<string, BaseLayer>();
  private backgroundLayers: Map<string, BaseLayer> = new Map<string, BaseLayer>();
  private vectorLayers: Map<string, VectorLayer> = new Map<string, VectorLayer>();

  private backgroundLayerGroup = new LayerGroup();
  private baseLayerGroup = new LayerGroup();
  private vectorLayerGroup = new LayerGroup();

  private prevBackgroundLayerIds: string[] = [];
  private prevLayerIdentifiers: string[] = [];

  private currentBackgroundLayerProjection: Projection;
  private currentBaseLayerProjection: Projection;

  constructor(private olMap: OlMap, private ngZone: NgZone, private httpXsrfTokenExtractor: HttpXsrfTokenExtractor) {
    this.currentBackgroundLayerProjection = this.olMap.getView().getProjection();
    this.currentBaseLayerProjection = this.olMap.getView().getProjection();
  }

  public init() {
    this.olMap.addLayer(this.backgroundLayerGroup);
    this.olMap.addLayer(this.baseLayerGroup);
    this.olMap.addLayer(this.vectorLayerGroup);
  }

  public destroy() {
    this.layers = new Map();
    this.backgroundLayers = new Map();
    this.vectorLayers = new Map();
    this.olMap.removeLayer(this.backgroundLayerGroup);
    this.olMap.removeLayer(this.baseLayerGroup);
    this.olMap.removeLayer(this.vectorLayerGroup);
  }

  public setBackgroundLayers(layers: LayerModel[], useProjection?: string) {
    this.prevBackgroundLayerIds = this.updateLayers(
      layers,
      this.backgroundLayers,
      this.prevBackgroundLayerIds,
      this.addBackgroundLayer.bind(this),
      this.removeBackgroundLayer.bind(this),
      this.getZIndexForBackgroundLayer.bind(this),
      this.backgroundLayerGroup,
      useProjection,
    );
  }

  private addBackgroundLayer(layer: LayerModel, zIndex?: number, useProjection?: string) {
    const olLayer = this.createLayer(layer, useProjection);
    if (olLayer === null) {
      return;
    }
    OlLayerHelper.setLayerProps(layer, olLayer);
    this.backgroundLayers.set(layer.id, olLayer);
    this.backgroundLayerGroup.getLayers().push(olLayer);
    olLayer.setZIndex(this.getZIndexForBackgroundLayer(zIndex));
    this.moveDrawingLayersToTop();
  }

  private removeBackgroundLayer(layerId: string) {
    this.removeLayerAndSource(layerId, this.backgroundLayerGroup, this.backgroundLayers);
  }

  private getZIndexForBackgroundLayer(zIndex?: number) {
    if (typeof zIndex === 'undefined' || zIndex === -1) {
      zIndex = this.backgroundLayerGroup.getLayers().getLength();
    }
    return zIndex;
  }

  public setLayers(layers: LayerModel[], useProjection?: string) {
    this.prevLayerIdentifiers = this.updateLayers(
      layers,
      this.layers,
      this.prevLayerIdentifiers,
      this.addLayer.bind(this),
      this.removeLayer.bind(this),
      this.getZIndexForLayer.bind(this),
      this.baseLayerGroup,
      useProjection,
    );
  }

  private updateLayers(
    layers: LayerModel[],
    currentLayerMap: Map<string, BaseLayer>,
    prevLayerIdentifiers: string[],
    addLayer: (layer: LayerModel, zIndex: number, useProjection?: string) => void,
    removeLayer: (id: string) => void,
    getZIndexForLayer: (zIndex?: number) => number,
    layerGroup: LayerGroup,
    useProjection?: string,
  ) {
    const layerGroupProjection = layerGroup === this.backgroundLayerGroup ? this.currentBackgroundLayerProjection : this.currentBaseLayerProjection;
    const layerIdentifiers = this.createLayerIdentifiers(layers);
    if (useProjection && useProjection !== layerGroupProjection.getCode()) {
      layerGroup.getLayers().clear();
      currentLayerMap.clear();
      if (layerGroup === this.backgroundLayerGroup) {
        this.currentBackgroundLayerProjection = getProjection(useProjection)!;
      } else {
        this.currentBaseLayerProjection = getProjection(useProjection)!;
      }
    } else if (ArrayHelper.arrayEquals(layerIdentifiers, prevLayerIdentifiers)) {
      return prevLayerIdentifiers;
    }
    const layerIds = layers.map(layer => layer.id);
    const layerIdSet = new Set(layerIds);
    const removableLayers: string[] = [];
    currentLayerMap.forEach((layer, id) => {
      if (!layerIdSet.has(id)) {
        removableLayers.push(id);
      }
    });
    removableLayers.forEach(id => removeLayer(id));
    const layerOrder = layerIds.reverse();
    layers
      .forEach(layer => {
        const zIndex = layerOrder.indexOf(layer.id);
        const existingLayer = currentLayerMap.get(layer.id);
        if (existingLayer) {
          this.updatePropertiesIfChanged(layer, existingLayer);
          existingLayer.setZIndex(getZIndexForLayer(zIndex));
          return;
        }
        addLayer(layer, zIndex, useProjection);
      });
    return layerIdentifiers;
  }

  public addLayer<LayerType extends LayerTypes>(layer: LayerModel, zIndex?: number, useProjection?: string): LayerType | null {
    const olLayer = this.createLayer(layer, useProjection);
    if (olLayer === null) {
      return null;
    }
    OlLayerHelper.setLayerProps(layer, olLayer);
    this.addLayerToMap(olLayer);
    olLayer.setZIndex(this.getZIndexForLayer(zIndex));
    this.moveDrawingLayersToTop();
    if (!LayerTypesHelper.isVectorLayer(layer)) {
      this.layers.set(layer.id, olLayer);
    }
    return olLayer as LayerType;
  }

  private getZIndexForLayer(zIndex?: number) {
    if (typeof zIndex === 'undefined' || zIndex === -1) {
      zIndex = this.getMaxZIndex();
    }
    zIndex += this.backgroundLayerGroup.getLayers().getLength();
    return zIndex;
  }

  // Create an identifier for each layer to quickly check if something changed and requires re-rendering
  private createLayerIdentifiers(layers: LayerModel[]): string[] {
    return layers.map(layer => {
      const opacity = layer.opacity !== undefined && layer.opacity !== null ? `${layer.opacity}` : undefined;
      const changingProps = [ layer.name, opacity ];
      if (LayerTypesHelper.isServiceLayer(layer)) {
        changingProps.push(layer.filter);
      }
      if (LayerTypesHelper.isWmsLayer(layer) && layer.selectedStyleName) {
        changingProps.push(layer.selectedStyleName);
      }
      return [ layer.id, ...changingProps.filter(Boolean) ].join('_');
    });
  }

  private updatePropertiesIfChanged(layer: LayerModel, olLayer: BaseLayer) {
    const currentOpacity = olLayer.getOpacity();
    const layerOpacity = typeof layer.opacity === 'undefined' ? 1 : layer.opacity / 100;
    if (currentOpacity !== layerOpacity) {
      olLayer.setOpacity(layerOpacity);
    }
    // For now, updating layer properties works for WMS only
    if (!LayerTypesHelper.isWmsLayer(layer) || !isOpenLayersWMSLayer(olLayer)) {
      return;
    }
    const checkPropChanges: Array<{ paramName: keyof WmsServiceParamsModel; layerKey: keyof WMSLayerModel }> = [
      { paramName: 'LAYERS', layerKey: 'name' },
      { paramName: 'STYLES', layerKey: 'selectedStyleName' },
    ];
    if (layer.serverType === ServerType.GEOSERVER) {
      // CQL filter is only supported by GeoServer, so only check for changes if the server type is GeoServer
      checkPropChanges.push({ paramName: 'CQL_FILTER', layerKey: 'filter' });
    }
    const currentParams: Partial<Record<keyof WmsServiceParamsModel, any>> = olLayer.getSource()?.getParams() || {};
    const changedParams: Partial<Record<keyof WmsServiceParamsModel, any>> = {};
    checkPropChanges
      .filter(({ paramName, layerKey }) => {
        return (currentParams[paramName] ?? '') !== (layer[layerKey] ?? '');
      })
      .forEach(({ paramName, layerKey }) => {
        changedParams[paramName] = layer[layerKey] ?? '';
      });
    if (Object.keys(changedParams).length === 0) {
      return;
    }
    olLayer.getSource()?.updateParams(changedParams);
  }

  public removeLayer(id: string) {
    this.removeLayerAndSource(id, this.baseLayerGroup, this.layers);
  }

  public removeLayers(layerIds: string[]) {
    layerIds.forEach(l => this.removeLayerAndSource(l, this.baseLayerGroup, this.layers));
  }

  public getLayer(layerId: string) {
    const layer = this.layers.get(layerId) || this.vectorLayers.get(layerId);
    if (!layer) {
      return;
    }
    return layer;
  }

  public addLayers(layers: LayerModel[]) {
    layers.forEach(layer => this.addLayer(layer));
  }

  public setLayerVisibility(layerId: string, visible: boolean) {
    const layer = this.layers.get(layerId);
    if (layer) {
      layer.setVisible(visible);
    }
  }

  public setLayerOpacity(layerId: string, opacity: number) {
    const layer = this.layers.get(layerId);
    if (layer) {
      layer.setOpacity(opacity / 100);
    }
  }

  public setLayerOrder(layerIds: string[]) {
    let zIndex = 1;
    for (const layerId of layerIds) {
      const layer = this.layers.get(layerId);
      if (layer) {
        layer.setZIndex(zIndex++);
      }
    }
  }

  private moveDrawingLayersToTop() {
    let zIndex = this.backgroundLayerGroup.getLayers().getLength() + this.baseLayerGroup.getLayers().getLength();
    this.vectorLayerGroup.getLayers().forEach(layer => {
      layer.setZIndex(++zIndex);
    });
  }

  public refreshLayer(layerId: string) {
    const layer = this.layers.get(layerId);
    if (!layer || !isPossibleRealtimeLayer(layer)) {
      return;
    }
    const source = layer.getSource();
    if (source instanceof ImageWMS || source instanceof TileWMS) {
      source.updateParams({ CACHE: Date.now() });
    }
    if (source instanceof WMTS || source instanceof XYZ) {
      const urls = (source.getUrls() || []).map(url => {
        const u = new URL(url);
        u.searchParams.set('CACHE', `${Date.now()}`);
        return u.toString();
      });
      source.setUrls(urls);
    }
  }

  public getLegendUrl(layerId: string): string {
    const layer = this.layers.get(layerId);
    if (!layer) {
      return '';
    }
    if (isOpenLayersWMSLayer(layer)) {
      return layer.getSource()?.getLegendUrl(
        undefined, {
          // Use WMS version 1.1.0 for higher GetLegendGraphic compatibility
          VERSION: '1.1.0',
          SLD_VERSION: '1.1.0',
        },
      ) || '';
    }
    return '';
  }

  private getMaxZIndex() {
    let maxZIndex = 0;
    this.baseLayerGroup.getLayers().forEach(layer => {
      maxZIndex = Math.max(maxZIndex, layer.getZIndex() || 0);
    });
    return maxZIndex;
  }

  private addLayerToMap(layer: BaseLayer) {
    if (isOpenLayersVectorLayer(layer)) {
      const vectorLayers = this.vectorLayerGroup.getLayers();
      vectorLayers.push(layer);
      this.vectorLayerGroup.setLayers(vectorLayers);
      return;
    }
    const layers = this.baseLayerGroup.getLayers();
    layers.push(layer);
    this.baseLayerGroup.setLayers(layers);
  }

  private removeLayerAndSource(
    layerId: string,
    layerGroup: LayerGroup,
    layerMap: Map<string, BaseLayer>,
  ) {
    const layer = layerMap.get(layerId) || this.vectorLayers.get(layerId);
    if (!layer) {
      return;
    }
    if (isOpenLayersVectorLayer(layer)) {
      this.removeVectorLayer(layer, layerId);
      return;
    }
    const layers = layerGroup.getLayers();
    layers.remove(layer);
    layerGroup.setLayers(layers);
    layerMap.delete(layerId);
  }

  private removeVectorLayer(layer: VectorLayer, layerId: string) {
    const vectorLayer = this.vectorLayers.get(layerId);
    if (vectorLayer) {
      vectorLayer.getSource()?.clear();
    }
    const vectorLayers = this.vectorLayerGroup.getLayers();
    vectorLayers.remove(layer);
    this.vectorLayerGroup.setLayers(vectorLayers);
    this.vectorLayers.delete(layerId);
    return;
  }

  private createLayer(layer: LayerModel, useProjection?: string): LayerTypes {
    if (LayerTypesHelper.isVectorLayer(layer)) {
      return this.createVectorLayer(layer);
    }
    const projection: Projection = useProjection ? getProjection(useProjection)! : this.olMap.getView().getProjection();
    const olLayer = OlLayerHelper.createLayer(layer, projection, this.ngZone, this.httpXsrfTokenExtractor);
    if (!olLayer) {
      return null;
    }
    if (typeof layer.opacity === 'number') {
      olLayer.setOpacity(layer.opacity / 100);
    }
    return olLayer;
  }

  private createVectorLayer(layer: VectorLayerModel): VectorLayer | null {
    const updateWhileAnimating = layer.updateWhileAnimating ?? false;
    const source = new VectorSource({ wrapX: true });
    const vectorLayer = new VectorLayer({ source, visible: layer.visible, updateWhileAnimating, updateWhileInteracting: updateWhileAnimating });
    // Set altitudeMode to clampToGround to display the layer on the terrain in 3D view
    vectorLayer.set('altitudeMode', 'clampToGround');
    this.vectorLayers.set(layer.id, vectorLayer);
    return vectorLayer;
  }
}
